import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ticketsAPI } from '../api/api';
import { useProject } from './ProjectContext';

const TicketContext = createContext();

export const useTickets = () => {
  const context = useContext(TicketContext);
  if (!context) {
    throw new Error('useTickets must be used within a TicketProvider');
  }
  return context;
};

// Alias for useTickets - some components use useTicket
export const useTicket = useTickets;

export const TicketProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [projectStatuses, setProjectStatuses] = useState([]); // Yeni: Proje status'larını saklamak için
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selectedProject } = useProject();
  const location = useLocation();

  // Ticket sayfaları - sadece bu sayfalarda ticket'ları yükle
  const ticketRoutes = ['/tickets', '/board', '/dashboard'];
  const shouldLoadTickets = ticketRoutes.some(route => location.pathname.startsWith(route));

  // Load tickets from API based on selected project
  const loadTickets = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);


      let response;
      const isBoardView = location.pathname.startsWith('/board');

      if (selectedProject && selectedProject.id) {
        // Seçili proje varsa, o projeye ait ticket'ları getir
        response = await ticketsAPI.getTicketsByProject(selectedProject.id, isBoardView);
      } else {
        // Seçili proje yoksa, tüm ticket'ları getir
        response = await ticketsAPI.getTickets();
      }

      if (response && response.success) {
        // API katmanı veriyi response.data içinde döner
        const resultData = response.data || response;

        if (resultData && resultData.tickets && typeof resultData.tickets === 'object') {
          const ticketArray = [];
          Object.keys(resultData.tickets).forEach(status => {
            if (Array.isArray(resultData.tickets[status])) {
              resultData.tickets[status].forEach(ticket => {
                ticketArray.push({ ...ticket, status });
              });
            }
          });
          setTickets(ticketArray);

          // Status'leri kaydet
          if (Array.isArray(resultData.statuses)) {
            setProjectStatuses(resultData.statuses);
          }
        } else if (resultData && Array.isArray(resultData.tickets)) {
          setTickets(resultData.tickets);
          if (Array.isArray(resultData.statuses)) {
            setProjectStatuses(resultData.statuses);
          }
        } else {
          setTickets([]);
          setProjectStatuses([]);
        }
      } else {
        setError(response?.message || 'Ticketlar yüklenirken hata oluştu');
        setTickets([]);
      }
    } catch (error) {
      setError('Ticketlar yüklenirken hata oluştu');
      setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [selectedProject?.id]); // Sadece selectedProject.id'yi dependency olarak kullan

  // Seçili proje değiştiğinde ticket'ları yeniden yükle
  useEffect(() => {
    // Sadece ticket sayfalarında ve seçili proje varsa ticket'ları yükle
    if (shouldLoadTickets && selectedProject && selectedProject.id) {
      loadTickets();
    } else {
      setLoading(false);
      setTickets([]);
    }
  }, [selectedProject?.id, loadTickets, shouldLoadTickets, location.pathname]);

  // Update ticket status (for drag and drop)
  const updateTicketStatus = React.useCallback(async (ticketId, newStatus) => {
    try {

      // Yeni Dinamik Mantık: 
      // Mevcut projectStatuses içinden sürüklenen status adıyla eşleşen gerçek ID'yi bul
      const findMatchingStatus = (name) => {
        if (!name) return null;
        const target = name.trim().toLocaleLowerCase('tr-TR');
        return projectStatuses.find(s =>
          s.status_name === name ||
          s.status_name.trim().toLocaleLowerCase('tr-TR') === target
        );
      };

      const matchingStatus = findMatchingStatus(newStatus);
      const statusId = matchingStatus ? matchingStatus.id : null;

      if (!statusId) {
        console.error(`❌ Status bulunamadı: "${newStatus}". Mevcut liste:`, projectStatuses);
        throw new Error(`Geçersiz status: ${newStatus}`);
      }


      // API call - backend'de status_id kullanılıyor
      const response = await ticketsAPI.updateTicket(ticketId, { status_id: statusId });

      if (!response.success) {
        // Hata durumunda geri al
        await loadTickets();
        throw new Error(response.message);
      } else {

        // Backend'den gelen güncel data ile state'i güncelle
        setTickets(prevTickets => {

          const updatedTickets = prevTickets.map(ticket =>
            ticket.id === parseInt(ticketId)
              ? {
                ...ticket,
                status: newStatus, // Manuel olarak status'u güncelle
                status_id: statusId // Status ID'yi de güncelle
              }
              : ticket
          );


          return updatedTickets;
        });
      }
    } catch (error) {
      // Revert on error
      await loadTickets();
      throw error;
    }
  }, [loadTickets, projectStatuses]);

  // Create new ticket
  const createTicket = React.useCallback(async (ticketData) => {
    try {

      // Seçili proje varsa ticket'a proje bilgisini ekle
      const ticketWithProject = {
        ...ticketData,
        project: selectedProject?.id || ticketData.project
      };

      const response = await ticketsAPI.createTicket(ticketWithProject);

      if (response.success) {
        // Yeni ticket'ı listeye ekle
        setTickets(prevTickets => [...prevTickets, response.data]);
        return response.data;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      throw error;
    }
  }, [selectedProject?.id]);

  // Update ticket
  const updateTicket = React.useCallback(async (ticketId, updateData) => {
    try {
      const response = await ticketsAPI.updateTicket(ticketId, updateData);

      if (response.success) {
        // Ticket'ı güncelle
        setTickets(prevTickets =>
          prevTickets.map(ticket =>
            ticket.id === ticketId
              ? { ...ticket, ...response.data }
              : ticket
          )
        );
        return response.data;
      } else {
        throw new Error(response.message || 'Ticket güncellenirken hata oluştu');
      }
    } catch (error) {
      throw error;
    }
  }, []);

  // Delete ticket
  const deleteTicket = React.useCallback(async (ticketId) => {
    try {

      const response = await ticketsAPI.deleteTicket(ticketId);

      if (response.success) {
        // Ticket'ı listeden kaldır
        setTickets(prevTickets =>
          prevTickets.filter(ticket => ticket.id !== ticketId)
        );
        return response.data;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      throw error;
    }
  }, []);

  const value = {
    tickets,
    projectStatuses, // Yeni state'i dışarı açalım
    loading,
    error,
    setTickets,
    loadTickets,
    createTicket,
    updateTicket,
    updateTicketStatus,
    deleteTicket
  };

  return (
    <TicketContext.Provider value={value}>
      {children}
    </TicketContext.Provider>
  );
};